import React, { useState, useEffect, useCallback } from 'react';
import { Spin, notification } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { OpPage } from 'components/customAntd/OpPage/OpPage';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import { deleteRequest, getRequest, patchRequest, postRequest } from 'api/apiClient';
import { hasPermission, transformNullToEmptyString } from 'utils/utils';
import OptionsForm from './tabs/OptionsForm';
import FieldsForm from './tabs/FieldsForm';
import CustomizationForm from './tabs/CustomizationForm';
import { InvitationDocument } from 'types/visitorInvitationTypes';
import { CustomFormButtons } from './CustomFormButtons';
import { createInvitationCustomFields, deleteInvitationCustomFields, fetchInvitationConfig, fetchInvitationCustomFields, updateInvitationConfig, updateInvitationCustomFields } from 'store/slices/visitorInvitationSlice';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { fetchCustomFields } from 'store/slices/customFieldsSlice';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

const Invitations: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const { invitationConfig, invitationCustomFields } = useSelector((state: RootState) => state.visitorInvitation);

    const hasInvitationRead = hasPermission(tokenScopeList, orgId, 'o', 'invitation:r');
    const hasInvitationWrite = hasPermission(tokenScopeList, orgId, 'o', 'invitation:w');

    const [activeKey, setActiveKey] = useState<string>('options');
    const [invitationDocuments, setInvitationDocuments] = useState<InvitationDocument[]>([]);
    const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null); // Manage a single file
    const [emailTemplates, setEmailTemplates] = useState<any[]>([]);
    const [emailTemplateAssigned, setEmailTemplateAssigned] = useState<any[]>([]);
    const [uploadedLogo, setUploadedLogo] = useState<string | null>(null);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [isPreRegistrationAllowed, setIsPreRegistrationAllowed] = useState<number>(0);

    const [form] = OpForm.useForm();

    const [savedValues, setSavedValues] = useState<any>(null);

    const { t } = useTranslation();

    useEffect(() => {
        if (isSubmitting) return;
        
        const initialValues: any = {
            invitationDocuments: invitationDocuments
                .filter(document => document.selected)
                .map(document => document.id),
            ...invitationConfig,
            allowPreRegistration: invitationConfig?.allowPreRegistration,
            customLogo: invitationConfig?.customLogo,
            customizationType: emailTemplateAssigned.length > 0 ? 'template' : 'default',
            emailTemplateId:
                emailTemplateAssigned.length > 0
                    ? emailTemplateAssigned[0].emailTemplate?.id?.toString()
                    : undefined,
        };

        // merge custom fields
        if (invitationCustomFields && invitationCustomFields.length > 0) {
            invitationCustomFields.forEach((field) => {
                initialValues[field.id] = {
                    included: field.included === 1,
                    required: field.required === 1,
                    configurable: field.configurable === 1,
                };
            });
        }

        setSavedValues(initialValues); // Update savedValues once data is ready
        form.setFieldsValue(initialValues); // Optionally set initial values for the form
    }, [invitationConfig, invitationDocuments, emailTemplateAssigned, invitationCustomFields, isSubmitting, form]);

    useEffect(() => {
        setActiveKey('options');
    }, []);

    // Fetch invitation config and documents
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                await dispatch(fetchInvitationConfig({ orgId }));
                await dispatch(fetchCustomFields({ orgId }));
                await dispatch(fetchInvitationCustomFields({ orgId }));

                const invitationDocuments = await getRequest(`/orgs/${orgId}/visitorInvitationDocument`);
                const emailTemplates = await getRequest(`/orgs/${orgId}/emailTemplates`);
                const emailTemplateAssigned = await getRequest(`/orgs/${orgId}/emailAssignment`);

                // Fetch logo data (used seperate try/catch because it returns 404 error when logo doesn't exist)
                try {
                    const logo = await axios.get(`${apiUrl}/orgs/${orgId}/visitorInvitation/logo`, {
                        headers: { Authorization: `${localStorage.getItem("authToken")}` },
                        responseType: 'blob'
                    });
                    const url = URL.createObjectURL(logo.data);
                    setUploadedLogo(url);
                } catch (error) {
                    setUploadedLogo(null);
                    console.error("Failed to fetch logo:", error);
                }

                const filteredAssignments = emailTemplateAssigned.data.filter(
                    (assignment: any) =>
                        assignment.emailTemplateType?.name === 'Invitation' &&
                        assignment.emailTemplateType?.id === 10
                );

                setInvitationDocuments(invitationDocuments.data);
                setEmailTemplates(emailTemplates.data);
                setEmailTemplateAssigned(filteredAssignments);

                setIsPreRegistrationAllowed(invitationConfig?.allowPreRegistration!);
            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [orgId, dispatch, invitationConfig?.allowPreRegistration, invitationConfig?.customMessage]);

    const tabItems = (invitationConfig && invitationDocuments) ? [
        {
            key: 'options',
            label: t('invitations.tabs.options'),
            children: <OptionsForm form={form} invitationDocuments={invitationDocuments}
                isPreRegistrationAllowed={isPreRegistrationAllowed} setIsPreRegistrationAllowed={setIsPreRegistrationAllowed} />,
        },
        {
            key: 'fields',
            label: t('invitations.tabs.fields'),
            children: <FieldsForm form={form} invitationConfig={invitationConfig} />,
        },
        {
            key: 'customization',
            label: t('invitations.tabs.customization'),
            children: <CustomizationForm form={form} setSelectedLogoFile={setSelectedLogoFile} emailTemplates={emailTemplates} emailTemplateAssigned={emailTemplateAssigned} uploadedLogo={uploadedLogo} />,
        },
    ] : [];

    const handleSubmit = useCallback(async ({ values, touchedValues }: IOnSubmitArgs) => {
        setIsSubmitting(true);
        try {
            if (touchedValues && Object.keys(touchedValues).length > 0) {
                const transformedValues = transformNullToEmptyString(touchedValues);

                // Extract `invitationDocuments` from transformedValues
                const { invitationDocuments, customizationType, emailTemplateId, logo, ...restValues } = transformedValues;

                const configForAPI = Object.fromEntries(
                    Object.entries(restValues).filter(([key]) => isNaN(Number(key)))
                );

                for (const key in touchedValues) {

                    const field = values[key];
                    const initialField = (savedValues) ? savedValues[key] : {};

                    const included = field?.included ?? false;
                    const required = field?.required ?? false;
                    const initialIncluded = initialField?.included ?? false;
                    const initialRequired = initialField?.required ?? false;

                    const isCustomField = !isNaN(Number(key));

                    if (isCustomField) {
                        // Add field if enabled
                        if (included && !initialIncluded) {
                            try {
                                await dispatch(createInvitationCustomFields({ orgId, fieldId: Number(key), required: required ? 1 : 0 }));
                            } catch (error) {
                                console.log(`Failed to add custom field ${key}:`, error);
                            }
                        }

                        // Remove field if disabled
                        if (!included && initialIncluded) {
                            try {
                                await dispatch(deleteInvitationCustomFields({ orgId, fieldId: Number(key) }));
                            } catch (error) {
                                console.log(`Failed to remove custom field ${key}:`, error);
                            }
                        }

                        // Update required if included is on and required status changed
                        if (included && initialIncluded && required !== initialRequired) {
                            try {
                                await dispatch(updateInvitationCustomFields({ orgId, fieldId: Number(key), required: required ? 1 : 0 }));
                            } catch (error) {
                                console.log(`Failed to update custom field ${key}:`, error);
                            }
                        }
                    }
                }

                // Dispatch only for non-custom fields
                if (Object.keys(configForAPI).length > 0) {
                    await dispatch(updateInvitationConfig({ orgId, config: configForAPI }));
                }

                // Handle invitationDocuments separately
                if (invitationDocuments) {
                    if (invitationDocuments.length !== 0) {
                        await patchRequest(`/orgs/${orgId}/visitorInvitationDocument`, { id: invitationDocuments });
                    } else {
                        await patchRequest(`/orgs/${orgId}/visitorInvitationDocument`, { id: [-1] });
                    }
                }

                if (savedValues && (values.customizationType !== savedValues.customizationType || values.emailTemplateId !== savedValues.emailTemplateId)) {
                    // Delete all email assignments where type.id === 10
                    for (const assignment of emailTemplateAssigned) {
                        if (assignment.emailTemplateType?.id === 10) {
                            await deleteRequest(`/orgs/${orgId}/emailAssignment/${assignment.id}`);
                        }
                    }

                    // Post if the type is template
                    if (values.customizationType === 'template' && emailTemplateId) {
                        await postRequest(`/orgs/${orgId}/emailAssignment`, {
                            emailTypeId: 10,
                            emailTemplateId: emailTemplateId,
                        });
                    }
                }

                // If a valid file exists, post to logo API
                if (selectedLogoFile) {
                    const formData = new FormData();
                    formData.append('photoUploadFile', selectedLogoFile);

                    await axios.post(`${apiUrl}/orgs/${orgId}/visitorInvitation/logo`, formData, {
                        headers: { Authorization: `${localStorage.getItem("authToken")}` },
                    });

                    setUploadedLogo(URL.createObjectURL(selectedLogoFile));
                }

                // Update savedValues after a successful save
                setSavedValues({ ...savedValues, ...transformedValues });

                // Fetch the latest invitationDocuments to ensure updated data
                const updatedDocuments = await getRequest(`/orgs/${orgId}/visitorInvitationDocument`);
                setInvitationDocuments(updatedDocuments.data);

                const updatedEmailTemplateAssigned = await getRequest(`/orgs/${orgId}/emailAssignment`);
                const filteredAssignments = updatedEmailTemplateAssigned.data.filter(
                    (assignment: any) =>
                        assignment.emailTemplateType?.name === 'Invitation' &&
                        assignment.emailTemplateType?.id === 10
                );
                setEmailTemplateAssigned(filteredAssignments);

                await dispatch(fetchInvitationCustomFields({ orgId }));

                // Reset Upload Queue
                form.resetFields(['logo']);
                setSelectedLogoFile(null);

                notification.success({
                    message: t('notification.success'),
                    description: t('invitations.notification.updateSuccess'),
                    placement: 'bottomRight',
                });
            }
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('invitations.notification.saveError'),
                placement: 'bottomRight',
            });
            console.error("Form submission failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [orgId, savedValues, emailTemplateAssigned, dispatch, t, selectedLogoFile, form]);

    // Custom Reset handler
    const handleReset = () => {
        if (savedValues && form.isFieldsTouched()) {
            form.setFieldsValue(savedValues);
            setIsPreRegistrationAllowed(Number(savedValues.allowPreRegistration!));

            // Reset Upload Queue
            form.resetFields(['logo']);
            setSelectedLogoFile(null);
        }
    };


    return (
        <OpPage title={t('invitations.title')} tooltip={t('invitations.tooltip')}>
            <OpForm
                form={form}
                onSubmit={handleSubmit}
                hasError={false}
                isReadOnly={!hasInvitationWrite && hasInvitationRead}
                defaultButtons={false}
            >
                {(loading || isSubmitting) ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Spin indicator={<LoadingOutlined spin />} />
                    </div>
                ) : (
                    <>
                        <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
                        <CustomFormButtons
                            form={form}
                            isFormLoading={loading}
                            isSubmitButtonLoading={isSubmitting}
                            onResetClick={handleReset}
                        />
                    </>
                )}
            </OpForm>
        </OpPage>
    );
}

export default Invitations;